/* eslint-disable @typescript-eslint/no-explicit-any */
import { Duration } from 'js-joda';
import { find, shuffle, split } from 'es-toolkit/compat';
import { oc } from 'ts-optchain';

import { AppSettings } from 'settings/AppSettings';
import { PatronLocationEntity } from 'components/patron-location/PatronLocation';
import { ServerError, ServerParseError } from '@apollo/client';
import { SharedAppConstants } from '@tock/shared-app-constants';
import { TOCK_TIMEZONE } from '@tock/time';
import { consumer, core, payloadToJSON, toJSON } from '@tock/coretypes';
import { getFullName } from '@tock/shared/utils/patron';
import { setAuthCookie } from '@tock/shared/utils/authCookieUtils';
import { validateShippingStateOrGetDefault } from '@tock/shared/utils/shippingStateUtils';
import { verifyCsrfToken } from '@tock/shared/utils/csrfTokenUtils';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const { AuthRequest } = protos;

interface State {
  activeAuth: {
    businessGroupId?: number;
    businessId?: number;
    partnerId?: number;
    site: core.SiteScope;
    chaseProductCode?: string;
  };
  activePartnerRole?: core.AuthPartnerResponse;
  appContext?: {
    domain: string;
    pathPrefix: string;
  };
  chaseUrContext?: {
    originToken: string;
    chaseSession: string;
  };
  authTypes: core.AuthProviders.ProviderType[];
  authorizedPartners?: core.AuthPartnerResponse[];
  config: consumer.ConsumerConfig | undefined;
  configRequestInProgress: boolean;
  csrfToken: core.CsrfToken | null;
  currency: string | undefined;
  detectedLocation?: core.DetectedLocation;
  graphqlError?: Error | ServerError | ServerParseError;
  inReactNative: boolean;
  fastRoutingBetweenContext: boolean;
  googleMapsLoaded: boolean;
  googleMapsLoadError?: Error;
  isInitialized: boolean;
  jwtToken: string;
  sessionToken: string;
  locale: string;
  restaurantCommunicationsOptIn: boolean;
  nextEmail: string | null;
  patron: core.Patron | null;
  patronAuthTypes?: string[] | null;
  patronLocation: {
    initialized: boolean;
    history: PatronLocationEntity[];
  };
  patronProfile: core.PatronProfile | null;
  patronProfilePlace?: google.maps.places.AutocompletePrediction;
  requestInProgress: boolean;
  settings: AppSettings;
  shippingState?: string;
  timeZone: string;
}

export const initialAppState: State = {
  activeAuth: {
    businessGroupId: 0,
    businessId: 0,
    site: 'EXPLORETOCK',
  },
  activePartnerRole: undefined,
  authTypes: [],
  authorizedPartners: undefined,
  config: undefined,
  configRequestInProgress: false,
  csrfToken: null,
  currency: 'USD',
  detectedLocation: undefined,
  googleMapsLoaded: false,
  googleMapsLoadError: undefined,
  graphqlError: undefined,
  inReactNative: false,
  fastRoutingBetweenContext: false,
  isInitialized: false,
  jwtToken: '',
  sessionToken: '',
  locale: 'en-US',
  restaurantCommunicationsOptIn: false,
  nextEmail: null,
  patron: null,
  patronAuthTypes: undefined,
  patronLocation: {
    history: [],
    initialized: false,
  },
  patronProfile: null,
  requestInProgress: false,
  settings: {} as any,
  shippingState: undefined,
  timeZone: TOCK_TIMEZONE,
};

const expirationInMs = Duration.ofDays(365).toMillis();

interface LocalOverrides {
  [locale: string]: string;
}

const localeOverrides: LocalOverrides = {};

export function detectUserTimeZoneName() {
  if (!BROWSER) {
    return 'Local Time';
  }

  try {
    return split(new Date().toLocaleTimeString(undefined, { timeZoneName: 'short' }).split(' ')[2]);
  } catch (_error) {
    return 'Local Time';
  }
}

export default function reducer(state: State = initialAppState, action: BaseTockAction): State {
  const { type, payload, context } = action;
  switch (type) {
    case AppConstants.CONFIG_FETCH_DONE: {
      const config = payloadToJSON<consumer.ConsumerConfig>(payload);
      let newState: Partial<State> = {
        config,
        activeAuth: state.activeAuth,
        appContext: {
          domain: config?.domain?.context === 'WWW' ? 'www' : context || '',
          pathPrefix: config?.domain?.context === 'WWW' ? '' : `/${context}`,
        },
        locale: 'en-US',
        currency: 'USD',
        timeZone: TOCK_TIMEZONE,
      };
      if (config.business) {
        newState = {
          ...newState,
          activeAuth: {
            ...state.activeAuth,
            businessGroupId: config.business.businessGroup?.id || undefined,
            businessId: config.business.id || undefined,
          },
          config: {
            ...config,
            business: {
              ...config.business,
              businessGroup: {
                ...config.business.businessGroup,
                business: shuffle(config.business.businessGroup?.business),
              },
            },
          },
          locale: config.business.locale
            ? localeOverrides[config.business.locale] || config.business.locale
            : undefined,
          currency: config.business.currencyCode || undefined,
          timeZone: config.business.timeZone || undefined,
        };
      } else if (newState.activeAuth) {
        delete newState.activeAuth.businessGroupId;
        delete newState.activeAuth.businessId;
      }
      return {
        ...state,
        isInitialized: true,
        configRequestInProgress: false,
        ...newState,
      };
    }
    case AppConstants.RESTORE_AUTH: {
      return {
        ...state,
        jwtToken: payload.jwtToken,
        sessionToken: payload.sessionToken,
        csrfToken: payload.csrfToken,
      };
    }
    case AppConstants.CREATE_ACCOUNT_DONE:
    case AppConstants.AUTHENTICATION_RESPONSE_DONE: {
      const authData = toJSON<core.AuthResponse>(payload);
      if (!authData.token) {
        return {
          ...state,
          requestInProgress: false,
        };
      }
      if (BROWSER) {
        setAuthCookie(window.location.hostname, authData.token, expirationInMs);
      }

      const csrfToken = verifyCsrfToken(authData.csrfToken);

      if ((oc(action) as any).request.type() === AuthRequest.Type.PARTNER_UI) {
        return {
          ...state,
          activeAuth: {
            ...state.activeAuth,
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            partnerId: authData.partner[0].partner!.id!,
          },
          activePartnerRole: authData.partner[0] as core.AuthPartnerResponse,
          authorizedPartners: authData.partner as core.AuthPartnerResponse[],
          jwtToken: authData.token,
          requestInProgress: false,
          csrfToken,
        };
      }
      return {
        ...state,
        jwtToken: authData.token,
        requestInProgress: false,
        csrfToken,
      };
    }
    case AppConstants.AUTHENTICATION_FETCH_ROLES_DONE: {
      const authData = toJSON<core.RoleResponse>(payload);
      return {
        ...state,
        activeAuth: {
          ...state.activeAuth,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          partnerId: authData.partner.length > 0 ? authData.partner[0].partner!.id! : undefined,
        },
        activePartnerRole: authData.partner[0] as core.AuthPartnerResponse,
        authorizedPartners: authData.partner as core.AuthPartnerResponse[],
        requestInProgress: false,
      };
    }
    case AppConstants.CHANGE_ACTIVE_PARTNER_ROLE: {
      const newPartner = find(state.authorizedPartners, ['partner.id', payload.id]);
      return {
        ...state,
        activePartnerRole: newPartner,
        activeAuth: {
          partnerId: payload.id,
          site: state.activeAuth.site,
        },
      };
    }
    case AppConstants.DETECT_LOCATION_DONE: {
      const detectedLocation = toJSON<core.DetectedLocation>(payload);
      return {
        ...state,
        detectedLocation,
      };
    }
    case AppConstants.SET_CHASE_PRODUCT_CODE: {
      return {
        ...state,
        activeAuth: {
          ...state.activeAuth,
          chaseProductCode: payload.productCode,
        },
      };
    }
    case AppConstants.ENABLE_REACT_NATIVE_MODE: {
      return {
        ...state,
        inReactNative: true,
        fastRoutingBetweenContext: true,
      };
    }
    case AppConstants.SET_BUSINESS_SCOPE: {
      return {
        ...state,
        activeAuth: {
          ...state.activeAuth,
          businessGroupId: undefined,
          businessId: payload,
        },
      };
    }
    case AppConstants.PATRON_SET_PROFILE_DONE: {
      const patron: core.Patron = toJSON<core.Patron>(payload);
      return {
        ...state,
        restaurantCommunicationsOptIn: !!payload.restaurantCommunicationsOptIn,
        patron,
        patronProfile: {
          ...(state.patronProfile as core.PatronProfile),
          patron,
        },
        requestInProgress: false,
      };
    }
    case AppConstants.PATRON_PROFILE_FETCH_PROFILE_DONE:
    case AppConstants.PATRON_PROFILE_SET_PROFILE_DONE: {
      const patronProfile = toJSON<core.PatronProfile>(payload);
      let place: google.maps.places.AutocompletePrediction | undefined;
      try {
        if (patronProfile.place) {
          place = JSON.parse(patronProfile.place) as google.maps.places.AutocompletePrediction;
        }
      } catch (_error) {
        place = undefined;
      }

      const patron = patronProfile.patron as core.Patron;
      if (BROWSER && window.FS) {
        // Avoid sending any Tock DB id to full story.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        window.FS.identify(patronProfile.patron!.email, {
          displayName: getFullName(patronProfile.patron),
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          email: patronProfile.patron!.email,
        });
      }
      return {
        ...state,
        restaurantCommunicationsOptIn: !!payload.restaurantCommunicationsOptIn,
        patron,
        patronProfile,
        patronProfilePlace: place,
        requestInProgress: false,
      };
    }
    case AppConstants.AUTHENTICATION_CLEARED_DONE: {
      if (BROWSER) {
        setAuthCookie(window.location.hostname, '', 0);
      }
      return {
        ...state,
        authorizedPartners: undefined,
        patron: null,
        jwtToken: '',
        requestInProgress: false,
      };
    }
    case AppConstants.PATRON_ACTIVE_EMAIL_CHANGE_DONE: {
      return {
        ...state,
        nextEmail: payload.nextEmail,
        requestInProgress: false,
      };
    }
    case AppConstants.PATRON_AUTH_TYPE_GET_DONE: {
      return {
        ...state,
        authTypes: payload.authType,
        requestInProgress: false,
      };
    }

    case AppConstants.CONFIG_FETCH_STARTING: {
      return {
        ...state,
        configRequestInProgress: true,
      };
    }
    case AppConstants.CONFIG_FETCH_FAILED: {
      return {
        ...state,
        configRequestInProgress: false,
      };
    }
    case AppConstants.AUTHENTICATION_METHODS_STARTING:
    case AppConstants.AUTHENTICATION_FETCH_ROLES_STARTING:
    case AppConstants.AUTHENTICATION_RESPONSE_STARTING:
    case AppConstants.CREATE_ACCOUNT_STARTING:
    case AppConstants.PATRON_ACTIVE_EMAIL_CHANGE_STARTING:
    case AppConstants.PATRON_AUTH_TYPE_GET_STARTING:
    case AppConstants.PATRON_EMAIL_CHANGE_CANCEL_STARTING:
    case AppConstants.PATRON_EMAIL_CHANGE_STARTING:
    case AppConstants.PATRON_INITIATE_EMAIL_CHANGE_STARTING:
    case AppConstants.PATRON_PROFILE_FETCH_PROFILE_STARTING:
    case AppConstants.PATRON_PROFILE_SET_IMAGE_STARTING:
    case AppConstants.PATRON_PROFILE_SET_PROFILE_STARTING:
    case AppConstants.PATRON_SET_PROFILE_STARTING:
    case SharedAppConstants.AUTHENTICATION_CSRF_REFRESH_STARTING:
      return {
        ...state,
        requestInProgress: true,
      };

    case AppConstants.AUTHENTICATION_METHODS_FAILED:
      return {
        ...state,
        patronAuthTypes: null,
        requestInProgress: false,
      };
    case AppConstants.AUTHENTICATION_RESPONSE_FAILED:
    case AppConstants.CREATE_ACCOUNT_FAILED:
    case AppConstants.PATRON_ACTIVE_EMAIL_CHANGE_FAILED:
    case AppConstants.PATRON_AUTH_TYPE_GET_FAILED:
    case AppConstants.PATRON_EMAIL_CHANGE_CANCEL_FAILED:
    case AppConstants.PATRON_EMAIL_CHANGE_FAILED:
    case AppConstants.PATRON_INITIATE_EMAIL_CHANGE_FAILED:
    case AppConstants.PATRON_PROFILE_FETCH_PROFILE_FAILED:
    case AppConstants.PATRON_PROFILE_SET_IMAGE_FAILED:
    case AppConstants.PATRON_PROFILE_SET_PROFILE_FAILED:
    case AppConstants.PATRON_SET_PROFILE_FAILED:
    case AppConstants.PATRON_INITIATE_EMAIL_CHANGE_DONE:
    case AppConstants.PATRON_PROFILE_SET_IMAGE_DONE:
    case AppConstants.PATRON_EMAIL_CHANGE_DONE:
    case AppConstants.PATRON_EMAIL_CHANGE_CANCEL_DONE:
    case SharedAppConstants.AUTHENTICATION_CSRF_REFRESH_FAILED:
      return {
        ...state,
        requestInProgress: false,
      };
    case AppConstants.AUTHENTICATION_METHODS_DONE: {
      const patronAuthTypes = payload;
      return {
        ...state,
        patronAuthTypes,
        requestInProgress: false,
      };
    }
    case AppConstants.SET_SETTINGS:
      return {
        ...state,
        settings: payload,
      };
    case AppConstants.SET_GOOGLE_MAPS_LOADED:
      return {
        ...state,
        googleMapsLoaded: payload,
      };
    case AppConstants.SET_GOOGLE_MAPS_LOAD_ERROR:
      return {
        ...state,
        googleMapsLoadError: payload,
      };

    case AppConstants.PATRON_LOCATION_HISTORY_UPDATED: {
      return {
        ...state,
        patronLocation: {
          ...state.patronLocation,
          history: payload,
          initialized: true,
        },
      };
    }
    case AppConstants.GRAPHQL_ERROR_OCCURED: {
      return {
        ...state,
        graphqlError: payload,
      };
    }
    case AppConstants.UPDATE_SHIPPING_STATE: {
      const validatedShippingState = validateShippingStateOrGetDefault(payload);
      return {
        ...state,
        shippingState: validatedShippingState,
      };
    }
    case SharedAppConstants.AUTHENTICATION_CSRF_REFRESH_DONE: {
      const csrfToken = toJSON<core.CsrfToken>(payload);
      return {
        ...state,
        csrfToken,
        requestInProgress: false,
      };
    }
    default:
      return state;
  }
}
